import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Package, AlertTriangle } from "lucide-react";
import { formatPrice } from "@/utils/currency";

const URGENCY_COLORS = {
  critical: "bg-red-500/15 text-red-400 border-red-500/30",
  high: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  medium: "bg-blue-500/15 text-blue-400 border-blue-500/30",
};

export function ProcurementReport({ procurementData }) {
  const { t } = useTranslation();
  const procSummary = procurementData?.summary || { total_items: 0, items_to_buy: 0, estimated_cost: 0 };

  return (
    <div className="space-y-4" data-testid="procurement-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.procurement.totalIngredients")}</p>
            <p className="text-3xl font-bold" data-testid="proc-total-items">{procSummary.total_items}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.procurement.needToBuy")}</p>
            <p className="text-3xl font-bold text-amber-500" data-testid="proc-items-to-buy">{procSummary.items_to_buy}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.procurement.estimatedCost")}</p>
            <p className="text-3xl font-bold" data-testid="proc-estimated-cost">{formatPrice(procSummary.estimated_cost)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.procurement.forecast")}</p>
            <p className="text-3xl font-bold">{procSummary.days_ahead || 7} {t("reports.procurement.days")}</p>
          </CardContent>
        </Card>
      </div>

      {procurementData?.suggestions?.length > 0 ? (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-base uppercase tracking-tight flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" /> {t("reports.procurement.suggestions")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 sm:p-6">
            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-border/20" data-testid="procurement-table">
              {procurementData.suggestions.map(s => (
                <div key={s.stock_id} className="p-3 space-y-2" data-testid={`proc-row-${s.stock_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{s.name}</span>
                    <Badge className={`${URGENCY_COLORS[s.urgency]} border text-xs shrink-0`}>
                      {s.urgency === "critical" && <AlertTriangle className="w-3 h-3 mr-1" />}
                      {t(`reports.procurement.${s.urgency}`)}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                    <span className="text-muted-foreground">{t("reports.procurement.currentStock")}: <strong className="text-foreground">{s.current_quantity} {s.unit}</strong></span>
                    <span className="text-muted-foreground">{t("reports.procurement.minimum")}: <strong className="text-foreground">{s.min_quantity} {s.unit}</strong></span>
                    <span className="text-muted-foreground">{t("reports.procurement.daysRemaining")}: <strong className={s.days_remaining <= 3 ? "text-red-400" : "text-foreground"}>{s.days_remaining >= 999 ? "--" : `${s.days_remaining}d`}</strong></span>
                    <span className="text-muted-foreground">{t("reports.procurement.suggestedQty")}: <strong className="text-foreground">{s.suggested_quantity} {s.unit}</strong></span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{s.supplier || "--"}</span>
                    <span className="font-bold">{formatPrice(s.estimated_cost)}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="procurement-table-desktop">
                <thead>
                  <tr className="border-b border-border/50 text-left text-muted-foreground">
                    <th className="pb-3 font-medium">{t("reports.procurement.urgency")}</th>
                    <th className="pb-3 font-medium">{t("reports.procurement.ingredient")}</th>
                    <th className="pb-3 font-medium text-right">{t("reports.procurement.currentStock")}</th>
                    <th className="pb-3 font-medium text-right">{t("reports.procurement.minimum")}</th>
                    <th className="pb-3 font-medium text-right">{t("reports.procurement.dailyConsumption")}</th>
                    <th className="pb-3 font-medium text-right">{t("reports.procurement.daysRemaining")}</th>
                    <th className="pb-3 font-medium text-right">{t("reports.procurement.suggestedQty")}</th>
                    <th className="pb-3 font-medium text-right">{t("reports.procurement.estCost")}</th>
                    <th className="pb-3 font-medium">{t("reports.procurement.supplier")}</th>
                  </tr>
                </thead>
                <tbody>
                  {procurementData.suggestions.map(s => (
                    <tr key={s.stock_id} className="border-b border-border/20 hover:bg-muted/30 transition-colors" data-testid={`proc-row-${s.stock_id}`}>
                      <td className="py-3">
                        <Badge className={`${URGENCY_COLORS[s.urgency]} border text-xs`}>
                          {s.urgency === "critical" && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {t(`reports.procurement.${s.urgency}`)}
                        </Badge>
                      </td>
                      <td className="py-3 font-medium">{s.name}</td>
                      <td className="py-3 text-right">{s.current_quantity} {s.unit}</td>
                      <td className="py-3 text-right text-muted-foreground">{s.min_quantity} {s.unit}</td>
                      <td className="py-3 text-right">{s.daily_consumption}</td>
                      <td className="py-3 text-right">
                        <span className={s.days_remaining <= 3 ? "text-red-400 font-bold" : ""}>
                          {s.days_remaining >= 999 ? "--" : `${s.days_remaining}d`}
                        </span>
                      </td>
                      <td className="py-3 text-right font-bold">{s.suggested_quantity} {s.unit}</td>
                      <td className="py-3 text-right">{formatPrice(s.estimated_cost)}</td>
                      <td className="py-3 text-muted-foreground">{s.supplier || "--"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border/50">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">{t("reports.procurement.stockOk")}</p>
            <p className="text-sm text-muted-foreground/70 mt-1">{t("reports.procurement.stockOkDesc")}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
